import { seo, pageTitle } from '@shared/seo.config'

interface SeoProps {
  title?: string
  description?: string
  /** Image for this page; left empty, the website's default image is used. */
  image?: string
  /** True for a page that should stay out of search results (an admin page, say). */
  noIndex?: boolean
}

/**
 * Meta tags for ONE page. Put <Seo title="..." /> at the top of every page
 * component.
 *
 * React 19 hoists <title>/<meta>/<link> into <head> no matter where in the tree
 * they are rendered — so react-helmet and friends are not needed.
 *
 * Note: these tags only exist AFTER JavaScript runs. Most AI crawlers do not
 * run JavaScript, and their copy is injected into the HTML by the Worker
 * (server/seo/index.ts). The two paths complement each other; keep both.
 */
export function Seo({ title, description, image, noIndex }: SeoProps) {
  const full = pageTitle(title)
  const desc = description ?? seo.description
  const img = image ?? seo.ogImage
  return (
    <>
      <title>{full}</title>
      <meta name="description" content={desc} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={full} />
      <meta property="og:description" content={desc} />
      <meta property="og:site_name" content={seo.siteName} />
      {img && <meta property="og:image" content={img} />}
      <meta name="twitter:card" content="summary_large_image" />
    </>
  )
}
